import type { UserInstrumentDto } from "@eu.mdotm.api/sphere";
import type { Map as ImmutableMap } from "immutable";
import type { PortfolioLabComposition, PortfolioLabInvestmentItem } from "../../../ctx";
import { labDiv, labMul } from "../../../utils/math";
import { type CompositionCellUnit, parseEditableCell } from "../editability";
import type { MergedCompositionRow } from "../rows";
import { applyWeightEdit } from "./editor";

export type ResolveWeightEditParams = {
	colId: string;
	/** The raw cell value: a cleared cell arrives as `null`. */
	newValue: unknown;
	row: MergedCompositionRow;
	investments: PortfolioLabInvestmentItem[];
	instrumentPool: ImmutableMap<string, UserInstrumentDto>;
	effectiveGroupBy: string;
};

export type ResolveWeightEditResult = {
	virtualUuid: string;
	updatedComposition: PortfolioLabComposition[];
};

type EditedColumn = { virtualUuid: string; unit: CompositionCellUnit };

export function resolveWeightEdit({
	colId,
	newValue,
	row,
	investments,
	instrumentPool,
	effectiveGroupBy,
}: ResolveWeightEditParams): ResolveWeightEditResult | null {
	if (typeof newValue !== "number" || Number.isNaN(newValue)) {
		return null;
	}
	const column = parseColumnId(colId);
	if (!column) {
		return null;
	}
	// The grid's `editable` callbacks use the same parser, so the grid and this resolver accept the same edits.
	const cell = parseEditableCell({ virtualUuid: column.virtualUuid, row, investments }, column.unit);
	if (!cell) {
		return null;
	}
	const weight = cell.unit === "monetary" ? labMul(labDiv(newValue, cell.investment.aum), 100) : newValue;

	const updatedComposition = applyWeightEdit(
		{
			composition: cell.investment.currentComposition,
			instrumentPool,
			groupBy: effectiveGroupBy,
			aum: cell.investment.aum,
		},
		cell.covered,
		weight,
	);
	return { virtualUuid: column.virtualUuid, updatedComposition };
}

function parseColumnId(colId: string): EditedColumn | null {
	if (colId.endsWith("_monetaryWeight")) {
		return { virtualUuid: colId.slice(0, -"_monetaryWeight".length), unit: "monetary" };
	}
	if (colId.endsWith("_weight")) {
		return { virtualUuid: colId.slice(0, -"_weight".length), unit: "weight" };
	}
	return null;
}
